import { useEffect, useState } from "react";
import AutoSeo from "@/components/AutoSeo";
import PageMeta from "@/components/PageMeta";
import { Link, useParams } from "react-router-dom";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";
import Media from "@/components/Media";
import ShareButtons from "@/components/ShareButtons";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";

export function News() {
  const [posts, setPosts] = useState([]);
  const [visible, setVisible] = useState(8);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);
  const shown = posts.slice(0, visible);
  const hasMore = visible < posts.length;
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
      <AutoSeo
        pageKey="news"
        fallbackTitle="News & cooking journal"
        fallbackDescription="Stories from the Recipe Filer kitchen — feature releases, recipe tips, and the people preserving their family cookbooks."
      />
      <p className="overline mb-4">Recipe Filer News</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-14 leading-[0.95]">Stories from<br />the kitchen.</h1>
      <div className="grid md:grid-cols-2 gap-8">
        {shown.map((p) => (
          <Link key={p.post_id} to={`/news/${p.slug}`} data-testid={`news-post-${p.slug}`} className="card-recipe hover-lift block overflow-hidden">
            {p.images?.[0] && (
              <div className="-m-6 mb-5 aspect-[16/10] overflow-hidden">
                <Media src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
              </div>
            )}
            <p className="overline mb-3">{p.author_name}</p>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl text-rf-ink mb-3 leading-tight">{p.title}</h2>
            <p className="text-rf-ink-soft leading-relaxed">{p.seo_summary}</p>
          </Link>
        ))}
      </div>
      {hasMore && (
        <div className="text-center mt-12">
          <button
            data-testid="news-show-more"
            onClick={() => setVisible((v) => v + 8)}
            className="btn-ink"
          >
            Show more stories ({posts.length - visible} remaining)
          </button>
        </div>
      )}
    </div>
  );
}

/* JSON-LD <Article> schema for SEO — emitted only on the public reader. */
function BlogArticleJsonLd({ post }) {
  useEffect(() => {
    if (!post?.slug) return;
    const id = "rf-news-article-schema";
    const data = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.seo_summary || "",
      articleBody: (post.body || "").slice(0, 5000),
      wordCount: (post.body || "").split(/\s+/).filter(Boolean).length,
      datePublished: post.created_at,
      dateModified: post.created_at,
      author: { "@type": "Person", name: post.author_name || "Recipe Filer" },
      publisher: {
        "@type": "Organization",
        name: "Recipe Filer",
        logo: { "@type": "ImageObject", url: "https://recipefiler.com/icon-512.png" },
      },
      image: post.images || [],
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://recipefiler.com/news/${post.slug}` },
      keywords: (post.tags || []).join(", "),
      url: `https://recipefiler.com/news/${post.slug}`,
      isPartOf: { "@type": "Blog", name: "Recipe Filer News", url: "https://recipefiler.com/news" },
    };
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.text = JSON.stringify(data);
    return () => { el?.remove(); };
  }, [post]);
  return null;
}

export function NewsPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [nav, setNav] = useState({ prev: null, next: null, more: [] });
  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => {});
    api.get(`/blog/${slug}/nav`).then((r) => setNav(r.data)).catch(() => setNav({ prev: null, next: null, more: [] }));
    // scroll to top on slug change — keeps the next-article feel snappy
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);
  if (!post) return <div className="max-w-4xl mx-auto p-10">Loading…</div>;

  // Render body with markdown-style [text](url) links + paragraph splitting
  const renderBody = (body) => {
    const paragraphs = (body || "").split(/\n{2,}/);
    const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
    return paragraphs.map((para, i) => {
      const parts = [];
      let last = 0; let m;
      while ((m = linkRe.exec(para)) !== null) {
        if (m.index > last) parts.push(para.slice(last, m.index));
        parts.push(<a key={`${i}-${m.index}`} href={m[2]} target="_blank" rel="noreferrer noopener" className="text-rf-orange font-bold underline">{m[1]}</a>);
        last = m.index + m[0].length;
      }
      if (last < para.length) parts.push(para.slice(last));
      return <p key={i} className="mb-5 text-rf-ink-soft leading-relaxed">{parts.length ? parts : para}</p>;
    });
  };

  return (
    <article className="max-w-3xl mx-auto px-6 md:px-10 py-20">
      <PageMeta
        title={post.seo_title || post.title}
        description={post.seo_description || post.seo_summary || `${post.title} — Recipe Filer News.`}
        keywords={post.seo_keywords || undefined}
        ogImage={post.images?.[0]}
      />
      <BlogArticleJsonLd post={post} />
      <BreadcrumbJsonLd
        items={[
          { name: "Home", url: "https://recipefiler.com/" },
          { name: "News", url: "https://recipefiler.com/news" },
          { name: post.title || "Post" },
        ]}
      />

      <Link
        to="/news"
        data-testid="news-back-to-list"
        className="text-sm font-bold text-rf-orange hover:underline mb-8 inline-block"
      >
        ← Back to all news
      </Link>
      <p className="overline mb-3">By {post.author_name}</p>
      <h1 className="font-display text-4xl md:text-6xl font-black leading-[1] text-rf-ink mb-6">{post.h1 || post.title}</h1>
      {post.h2 && <p className="text-xl md:text-2xl text-rf-ink-soft mb-10 leading-snug font-display">{post.h2}</p>}
      {post.images?.[0] && (
        <div className="rounded-2xl overflow-hidden mb-10 aspect-[16/9]">
          <Media src={post.images[0]} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="text-lg">{renderBody(post.body)}</div>
      {post.tags?.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-2">
          {post.tags.map((t) => <span key={t} className="chip">{t}</span>)}
        </div>
      )}

      {/* Share row — visible on every published article */}
      <div className="mt-10 pt-6 border-t border-rf-ink/10">
        <ShareButtons
          url={typeof window !== "undefined" ? window.location.href : ""}
          ogPath={`/api/share/news/${post.slug}`}
          title={post.title}
          summary={post.seo_summary || ""}
          dataTestidPrefix="news-share"
        />
      </div>

      {/* Prev / Next pager — keeps readers inside the News section */}
      <nav
        data-testid="news-prev-next"
        aria-label="More articles"
        className="mt-16 pt-10 border-t border-rf-ink/10 grid sm:grid-cols-2 gap-4"
      >
        {nav.prev ? (
          <Link
            to={`/news/${nav.prev.slug}`}
            data-testid="news-prev-link"
            className="card-recipe hover-lift !p-5 group"
          >
            <p className="overline mb-2 inline-flex items-center gap-1"><CaretLeft size={12} weight="bold" /> Previous</p>
            <p className="font-display font-extrabold text-rf-ink leading-snug group-hover:text-rf-orange transition-colors">{nav.prev.title}</p>
          </Link>
        ) : <div className="hidden sm:block" />}
        {nav.next ? (
          <Link
            to={`/news/${nav.next.slug}`}
            data-testid="news-next-link"
            className="card-recipe hover-lift !p-5 text-right group"
          >
            <p className="overline mb-2 inline-flex items-center gap-1 justify-end w-full">Next <CaretRight size={12} weight="bold" /></p>
            <p className="font-display font-extrabold text-rf-ink leading-snug group-hover:text-rf-orange transition-colors">{nav.next.title}</p>
          </Link>
        ) : null}
      </nav>

      {/* 3 next stories below — keeps the read-three-things momentum */}
      {nav.more && nav.more.length > 0 && (
        <section
          data-testid="news-more-stories"
          aria-label="Next stories"
          className="mt-12"
        >
          <h2 className="overline mb-4">Up next from the kitchen</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {nav.more.map((m) => (
              <Link
                key={m.post_id}
                to={`/news/${m.slug}`}
                data-testid={`news-more-${m.slug}`}
                className="card-recipe hover-lift block overflow-hidden !p-0 group"
              >
                {m.images?.[0] ? (
                  <div className="aspect-[16/10] overflow-hidden">
                    <Media src={m.images[0]} alt={m.title} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-[16/10] bg-rf-cream-alt" aria-hidden="true" />
                )}
                <div className="p-4">
                  <p className="overline mb-2 text-xs">{m.author_name}</p>
                  <p className="font-display font-extrabold text-rf-ink leading-snug group-hover:text-rf-orange transition-colors line-clamp-3">{m.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
